import React, { useCallback, useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import Comment from "./Comment";
import useUserStore from "../../zustand/UseUserStore";
import useNewsfeed from "../../zustand/UseNewsfeed";

const CommentsSection = ({
	itemId,
	loading,
	isCompact,
	isOwnPost,
	postType,
}) => {
	const [commentText, setCommentText] = useState("");
	const [replyId, setReplyId] = useState(null);
	const [scrollTop, setScrollTop] = useState(0);

	// Intersection observer for infinite scroll
	const observer = useRef();
	const page = useRef(1);
	const userStore = useUserStore();
	const newsfeed = useNewsfeed();
	const comments = newsfeed.comments[itemId];
	const pinnedComments = newsfeed.pinnedComments[itemId];

	console.log("💬 CommentsSection postType:", postType);

	// Sort comments to show pinned ones first
	const sortedComments = comments
		? [...comments].sort((a, b) => {
				// First by pinned status (pinned comments first)
				if (
					pinnedComments?.CommentID === a.CommentID &&
					pinnedComments?.CommentID !== b.CommentID
				) {
					return -1;
				}
				if (
					pinnedComments?.CommentID !== a.CommentID &&
					pinnedComments?.CommentID === b.CommentID
				) {
					return 1;
				}

				// Then by newest first
				return (
					new Date(b.timestamp || b.created_at) -
					new Date(a.timestamp || a.created_at)
				);
			})
		: [];

	const hasMore = newsfeed.hasMoreComments[itemId];

	// Set up intersection observer for infinite scrolling
	const lastItemRef = useCallback(
		(node) => {
			if (loading) {
				return;
			}
			if (observer.current) {
				observer.current.disconnect();
			}

			observer.current = new IntersectionObserver((entries) => {
				const infiniteScroll = document.getElementById("infinite-scroll");
				if (infiniteScroll && infiniteScroll.scrollTop > 0) {
					setScrollTop(infiniteScroll.scrollTop);
				}
				if (entries[0].isIntersecting && hasMore) {
					newsfeed.loadComments(itemId, page.current + 1);
					page.current++;
				}
			});

			if (node) {
				observer.current.observe(node);
			}
		},
		[hasMore, newsfeed.loadComments, loading, itemId]
	);

	useEffect(() => {
		if (scrollTop > 0) {
			const infiniteScroll = document.getElementById("infinite-scroll");
			if (infiniteScroll) {
				infiniteScroll.scrollTop = parseInt(scrollTop);
			}
			setScrollTop(0);
		}
	}, [comments]);

	return (
		<motion.div
			initial={{ opacity: 0, height: 0 }}
			animate={{ opacity: 1, height: "auto" }}
			exit={{ opacity: 0, height: 0 }}
			transition={{ duration: 0.3, ease: "easeOut" }}
			className="w-full"
			style={{ overflow: "hidden" }}
		>
			<div className="w-full border-t border-border pt-2 dark:border-border">
				{/* Comment input */}
				{userStore.getAccessLevel() > 1 && (
					<motion.div
						className="mb-3 flex gap-2"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.1, duration: 0.2 }}
					>
						<Avatar className="h-8 w-8 border border-border dark:border-border">
							<AvatarFallback className="bg-muted text-muted-foreground dark:bg-muted dark:text-muted-foreground">
								{userStore.user.Username}
							</AvatarFallback>
							<AvatarImage src={userStore.user.AvatarURL || userStore.avatar} />
						</Avatar>
						<div className="flex flex-1 gap-2">
							<input
								type="text"
								placeholder="Add a comment..."
								className="flex-1 rounded-md border border-border bg-background px-3 py-1 text-sm text-foreground placeholder:text-muted-foreground dark:border-border dark:bg-background dark:text-foreground dark:placeholder:text-muted-foreground"
								id={`comment-input-${itemId}`}
								onClick={(e) => e.stopPropagation()}
								value={commentText}
								onChange={(e) => setCommentText(e.target.value)}
							/>
							<Button
								size="sm"
								onClick={(e) => {
									e.stopPropagation();
									newsfeed.handleAddComment(
										itemId,
										commentText,
										0,
										0,
										"Text",
										postType
									);
									setCommentText("");
								}}
								className="bg-purple-600 text-white hover:bg-purple-700 dark:bg-purple-600 dark:hover:bg-purple-700"
							>
								Post
							</Button>
						</div>
					</motion.div>
				)}

				{/* Comments list */}
				<motion.div
					className={`space-y-3 ${isCompact ? "max-h-60" : ""} overflow-y-auto`}
					id={`infinite-scroll`}
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ delay: 0.2, duration: 0.2 }}
				>
					{loading ? (
						<div className="flex justify-center py-3">
							<Skeleton className="h-5 w-40 bg-muted dark:bg-muted" />
						</div>
					) : sortedComments.length > 0 ? (
						<>
							{pinnedComments && (
								<Comment
									key={pinnedComments.CommentID}
									comment={pinnedComments}
									replyId={replyId}
									setReplyId={setReplyId}
									isOwnPost={isOwnPost}
									postType={postType}
									itemId={itemId}
								/>
							)}
							{sortedComments.map((comment) => {
								if (!comment.IsPinned) {
									return (
										<Comment
											key={comment.CommentID}
											comment={comment}
											replyId={replyId}
											setReplyId={setReplyId}
											isOwnPost={isOwnPost}
											postType={postType}
											itemId={itemId}
										/>
									);
								}
							})}
							{/* Last item reference for intersection observer */}
							{hasMore && !loading && <div ref={lastItemRef} className="h-1" />}
						</>
					) : (
						<p className="text-center text-sm text-muted-foreground dark:text-muted-foreground">
							No comments yet. Be the first to comment!
						</p>
					)}
				</motion.div>
			</div>
		</motion.div>
	);
};

export default CommentsSection;
